import { useEffect, useRef } from 'react';
import { useAtom } from 'jotai';

import { getCampaignExternalChanges } from '@/lib/apis';
import { syncedCampaignCheckTimestampsAtom } from '@/store';
import { type CampaignData } from '@/lib/types';
import { shouldRunAutoSync } from '@/lib/auto-reverse-sync';
import { useReverseSync } from '@/hooks/useReverseSync';

/**
 * Auto-pull on campaign open (#5566 Req 1). On mount, cheaply ask the backend whether Google Ads
 * changed since Glitch and Google Ads last agreed; if so, trigger the existing reverse-sync pull.
 *
 * - Throttled to once per 15 min per campaign via {@link syncedCampaignCheckTimestampsAtom}.
 * - StrictMode/remount guarded (acts at most once per campaign id per mount).
 * - Silent and best-effort: the pull runs through a silent useReverseSync so it does not double the
 *   completion toast against the manual button's instance; detection failures are swallowed.
 *
 * Invoke ONCE from the shared campaign layout (it does not remount on campaign↔ad navigation).
 */
export function useAutoReverseSync(campaign: CampaignData) {
  const campaignId = campaign.id;
  const campaignState = campaign.state;
  const [checkTimestamps, setCheckTimestamps] = useAtom(syncedCampaignCheckTimestampsAtom);
  const { dispatch, isPending } = useReverseSync(campaign, { silent: true });
  const ranForRef = useRef<number | null>(null);

  useEffect(() => {
    // External campaigns never auto-pull (#6381): the pull endpoint is
    // write-guarded against them (403), so dispatching was a silent dead call.
    // Their structure freshness rides the nightly chord once imported, plus the
    // manual Import/Refresh button.
    if (campaign.origin === 'external') return;
    // StrictMode double-invokes effects in dev; only act once per campaign id per mount.
    if (ranForRef.current === campaignId) return;
    if (!shouldRunAutoSync({ state: campaignState, lastCheckedMs: checkTimestamps[campaignId], nowMs: Date.now() })) {
      return;
    }
    ranForRef.current = campaignId;
    // Stamp before the await so a fast remount / StrictMode second pass can't re-fire the GA read.
    setCheckTimestamps((prev) => ({ ...prev, [campaignId]: Date.now() }));

    void (async () => {
      try {
        const { has_external_changes } = await getCampaignExternalChanges(campaignId);
        if (has_external_changes) await dispatch();
      } catch {
        // Best-effort: a failed detect or pull must never surface or block the page.
      }
    })();
    // checkTimestamps/dispatch are intentionally excluded: stamping the atom must not re-trigger
    // this effect, and the run is gated per campaign id by ranForRef + the throttle.
  }, [campaignId, campaignState, campaign.origin]);

  return { isPending };
}
